import Check from "@mui/icons-material/Check";
import HighlightOff from "@mui/icons-material/HighlightOff";
import { Box, Button, ChipProps, Typography, styled } from "@mui/material";
import _ from "lodash";
import { ReactElement, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { CampaignContactTag } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import TagChip from "~frontend/components/TagChip";
import theme, { palette } from "~frontend/theme";

import { CampaignForTexter } from "./types";

type TagOption = { id: string; name: string };

const TagSectionHeader = styled(Box)({
  fontSize: "14px",
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  height: "32px",
});

const TagSection = styled(Box)({
  marginBottom: theme.spacing(5),
});

const SavedTag = (props: ChipProps): ReactElement => {
  const [hover, setHover] = useState(false);
  return (
    <TagChip
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      sx={{
        backgroundColor: palette.darkGreen,
        color: "white",
        fontWeight: 600,
        margin: "2px",
        border: "2px solid transparent",
        "&:hover": {
          backgroundColor: palette.darkGreen,
        },
      }}
      icon={
        hover ? (
          <HighlightOff
            sx={{
              width: "16px",
              height: "14px",
              paddingRight: "2px",
              fill: "white",
            }}
          />
        ) : (
          <Check
            sx={{
              width: "16px",
              height: "14px",
              paddingRight: "2px",
              fill: "white",
            }}
          />
        )
      }
      {...props}
    />
  );
};

const RemovedSavedTag = (props: ChipProps): ReactElement => (
  <TagChip
    sx={{
      color: palette.orange,
      backgroundColor: palette.white,
      fontWeight: 600,
      margin: "2px",
      border: "2px solid",
    }}
    icon={
      <HighlightOff
        sx={{
          width: "16px",
          height: "14px",
          paddingRight: "2px",
          fill: palette.orange,
        }}
      />
    }
    {...props}
  />
);

const SelectedTag = (props: ChipProps): ReactElement => (
  <TagChip
    sx={{
      background: "none",
      border: `2px solid ${palette.darkGreen}`,
      color: palette.darkGreen,
      fontWeight: 600,
      margin: "2px",
    }}
    icon={
      <Check
        sx={{
          width: "16px",
          height: "14px",
          paddingRight: "2px",
        }}
      />
    }
    {...props}
  />
);

export default function Tags({
  contactTags,
  campaign,
  unsavedTagIds,
  removedSavedTagIds,
  addUnsavedTag,
  removeUnsavedTag,
  removeSavedTag,
  cancelRemoveSavedTag,
  mutationPending,
  onSaveTags,
}: {
  contactTags?: Omit<CampaignContactTag, "createdAt" | "createdBy">[];
  campaign?: CampaignForTexter;
  unsavedTagIds: string[];
  removedSavedTagIds: string[];
  addUnsavedTag: (tagId: string) => void;
  removeUnsavedTag: (tagId: string) => void;
  removeSavedTag: (tagId: string) => void;
  cancelRemoveSavedTag: (tagId: string) => void;
  mutationPending: boolean;
  onSaveTags: () => void;
}): ReactElement {
  const allTags = useMemo(() => {
    const campaignAndResponseTags = (campaign?.tags || []).concat(
      (campaign?.responseScriptGroups || [])
        .flatMap(group => group.memberships)
        .flatMap(membership => membership.script.tags)
    );

    return _(campaignAndResponseTags)
      .uniqBy(tag => tag.id)
      .sortBy(tag => tag.name)
      .value();
  }, [campaign?.tags, campaign?.responseScriptGroups]);

  const savedTags: TagOption[] = (contactTags || []).map(contactTag => ({
    id: contactTag.tag.id,
    name: contactTag.tag.name,
  }));
  const savedTagIds = new Set(savedTags.map(tag => tag.id));

  const availableTags: TagOption[] = allTags.filter(
    tag => !savedTagIds.has(tag.id)
  );

  return (
    <Box>
      <TagSectionHeader>
        <FlexBox justifyContent="space-between" alignItems="center">
          <Box sx={{ flex: 1 }}>
            <FormattedMessage
              defaultMessage="Saved Tags"
              description="Header for section displaying tags applied to a contact"
            />
          </Box>
          <Button
            sx={{
              visibility:
                unsavedTagIds.length > 0 || removedSavedTagIds.length > 0
                  ? undefined
                  : "hidden",
            }}
            size="small"
            color="primary"
            variant="contained"
            onClick={onSaveTags}
            disabled={mutationPending}
          >
            <FormattedMessage
              defaultMessage="Save Tags"
              description="Button text to save tags for a contact"
            />
          </Button>
        </FlexBox>
      </TagSectionHeader>
      <TagSection>
        {savedTags.length === 0 ? (
          <Typography display="inline" sx={{ fontStyle: "italic" }}>
            <FormattedMessage
              defaultMessage="No saved tags"
              description="Message shown when there are no saved tags applied to a contact"
            />
          </Typography>
        ) : (
          <>
            {savedTags.map(tag =>
              removedSavedTagIds.includes(tag.id) ? (
                <RemovedSavedTag
                  key={tag.id}
                  label={tag.name}
                  onClick={() => cancelRemoveSavedTag(tag.id)}
                />
              ) : (
                <SavedTag
                  key={tag.id}
                  label={tag.name}
                  onClick={() => removeSavedTag(tag.id)}
                />
              )
            )}
          </>
        )}
      </TagSection>
      <TagSectionHeader>
        <FormattedMessage
          defaultMessage="Unsaved Tags"
          description="Header for section displaying tags that can be applied to a contact"
        />
      </TagSectionHeader>

      <TagSection>
        {availableTags.length === 0 ? (
          <Typography display="inline" sx={{ fontStyle: "italic" }}>
            <FormattedMessage
              defaultMessage="No available tags"
              description="Message shown when there are no tags that can be applied to a contact"
            />
          </Typography>
        ) : (
          <>
            {availableTags.map(tag =>
              unsavedTagIds.includes(tag.id) ? (
                <SelectedTag
                  key={tag.id}
                  label={tag.name}
                  onClick={() => {
                    removeUnsavedTag(tag.id);
                  }}
                />
              ) : (
                <TagChip
                  sx={{ margin: "2px", border: "2px solid transparent" }}
                  key={tag.id}
                  label={tag.name}
                  onClick={() => {
                    addUnsavedTag(tag.id);
                  }}
                />
              )
            )}
          </>
        )}
      </TagSection>
    </Box>
  );
}
